/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { ADD_SERIAL_NO, GET_LABEL_STATUS } from "../graphql";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const is = require("is_js");
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IAddSerialNoMethods extends IPluginMethodMap {
  addSerialNo(args: any): Promise<any>;
}

export class AddSerialNoService implements IAgentPlugin {
  readonly methods: IAddSerialNoMethods;
  private pollingTime = 10000;

  constructor() {
    this.methods = {
      addSerialNo: this.addSerialNo.bind(this),
    };
  }

  async addSerialNo(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: ADD_SERIAL_NO,
          variables: {
            input: {
              presentation: args?.presentation,
              section: args?.section,
              sectionOptType: args?.sectionOptType,
            },
          },
          fetchPolicy: "no-cache",
        });

        if (
          res?.data?.addSerialNo.jobId &&
          is.existy(res?.data?.addSerialNo.jobId)
        ) {
          return this.checkStatus(res?.data?.addSerialNo.jobId);
        } else {
          throw new Error("Some unexpected error occurred");
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private async checkStatus(jobId: string, retries = 25) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      const res = await client.query({
        query: GET_LABEL_STATUS,
        variables: {
          jobId: jobId,
        },
        fetchPolicy: "network-only",
      });

      if (res?.data?.getLabelStatus?.message === "success") {
        return res?.data?.getLabelStatus?.pdfUrl;
      } else if (res?.data?.getLabelStatus?.message === "pending" && res?.data?.getLabelStatus?.message === "PaymentNotComplete") {
        if (retries > 0) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              this.checkStatus(jobId, retries - 1)
                .then(resolve)
                .catch(reject);
            }, this.pollingTime || 10000);
          });
        } else {
          throw new Error("Exceeded retry limit for checking status");
        }
      } else {
        throw new Error("Some unexpected error occurred");
      }
    } catch (err: any) {
      console.error("checkStatus error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
